import React, { useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Tooltip,
  IconButton,
  Paper,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import Draggable from "react-draggable";
import { styled } from "@mui/system";
import PasswordField from "./PasswordField.jsx"; // ✅ your reusable field
import { useTranslation } from "react-i18next";

/* ───────────── Styled modal container ───────────── */
const StyledContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: "white",
  borderRadius: "8px",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0px 4px 10px rgba(0,0,0,0.4)",
  border: "1px solid #304fff",
}));

/* ───────────── Draggable PaperComponent ───────────── */
function PaperComponent(props) {
  const nodeRef = useRef(null);

  return (
    <Draggable handle=".handle" nodeRef={nodeRef} cancel={'[class*="MuiDialogContent-root"]'}>
      <StyledContainer ref={nodeRef} {...props} />
    </Draggable>
  );
}

/* ───────────── PasswordDialog component ───────────── */
export default function PasswordDialog({
  open,
  onClose,
  onSubmit,
  pwdValue,
  setPwdValue,
  showPassword,
  handleClickShowPassword,
  handleMouseDownPassword,
  dir = "rtl",
  pwdError,
  titleKey,
  // Props for old password field (when user is changing their own password)
  UserRole = null,
  oldPwdValue,
  setOldPwdValue,
  oldPwdError,
}) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperComponent={PaperComponent}
      PaperProps={{ style: { direction: dir, zIndex: 1301 } }}
      disableEnforceFocus
      keepMounted
    >
      {/* Header: title on start (right in RTL), CancelIcon on end (left in RTL) */}
      <div
        className="handle"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          fontWeight: "bold",
          width: "100%",
          cursor: "move",
        }}
      >
        <DialogTitle sx={{ p: 0, m: 0, flex: 1 }}>
          {t(titleKey || "EnterInternalPasswordForCase")}
        </DialogTitle>
        <Tooltip title={t("Exit")}>
          <IconButton onClick={onClose} size="small" sx={{ flexShrink: 0 }}>
            <CancelIcon sx={{ color: "#304FFF" }} />
          </IconButton>
        </Tooltip>
      </div>

      {/* Old Password Field - shown when user is changing their own password (Client/SubAccountant) */}
      {UserRole && (
        <PasswordField
          value={oldPwdValue}
          onChange={(e) => setOldPwdValue(e.target.value)}
          direction={dir}
          label={t("OldPassword")}
          error={!!oldPwdError}
          helperText={oldPwdError}
        />
      )}

      {/* New Password Field */}
      <PasswordField
        value={pwdValue}
        onChange={(e) => setPwdValue(e.target.value)}
        showPassword={showPassword}
        handleClickShowPassword={handleClickShowPassword}
        handleMouseDownPassword={handleMouseDownPassword}
        direction={dir}
        label={UserRole ? t("NewPassword") : t("enterPassword")}
        error={!!pwdError}
        helperText={pwdError} 
      />

      {/* Footer buttons */}
      <DialogActions sx={{ justifyContent: "center", mt: 2 }}>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button onClick={onSubmit} variant="contained">
          {t("Confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
